import { useMemo, useState } from "react";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Building2, Store, Target, GitBranch, Users, DollarSign, UserCheck,
  SlidersHorizontal, Calculator, TrendingUp, BarChart3, Wallet,
  ArrowLeftRight, Shield, IdCard, History,
  Menu, LucideIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { useEstructuraComisionesSubmenus } from "@/hooks/usePortalEstructuraComisiones/useEstructuraComisionesSubmenus";
import { useProjectAdminImpersonation, ProjectAdminImpersonationProvider } from "@/contexts/ProjectAdminImpersonationContext";
import { ProjectAdminImpersonationSelector } from "./ProjectAdminImpersonationSelector";
import { SimulatorProvider } from "@/lib/portal-estructura-comisiones/stores/SimulatorContext";
import { InventoryProvider } from "@/lib/portal-estructura-comisiones/stores/InventoryContext";
import { CompetitorsProvider } from "@/lib/portal-estructura-comisiones/stores/CompetitorsContext";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";

interface NavItem { label: string; path: string; icon: LucideIcon }
interface NavGroup { label: string; items: NavItem[] }

const BASE = "/admin/portal-estructura-comisiones";

// Los íconos no viven en BD — única fuente hardcodeada, una entrada por cada
// `vista_front_end` real que existe hoy en `submenus` (menu_id=35).
const PATH_ICONS: Record<string, LucideIcon> = {
  [`${BASE}/projects`]: Building2,
  [`${BASE}/channels`]: Store,
  [`${BASE}/benchmark`]: Target,
  [`${BASE}/org-chart`]: GitBranch,
  [`${BASE}/structure`]: Users,
  [`${BASE}/roles-sueldos`]: IdCard,
  [`${BASE}/commissions`]: DollarSign,
  [`${BASE}/historial-comisiones`]: History,
  [`${BASE}/incentivos-dinamicos`]: UserCheck,
  [`${BASE}/scenarios`]: SlidersHorizontal,
  [`${BASE}/distribucion`]: DollarSign,
  [`${BASE}/unit-commission`]: Calculator,
  [`${BASE}/flujo-comercial`]: TrendingUp,
  [`${BASE}/financieros`]: BarChart3,
  [`${BASE}/costos-comerciales`]: Wallet,
  [`${BASE}/simulador-ingresos`]: UserCheck,
  [`${BASE}/comparador`]: ArrowLeftRight,
  [`${BASE}/competitividad`]: Shield,
};

// `submenus.orden` codifica la sección por el centenar (100=Configuración, ...).
const ORDEN_GROUP_LABEL: Record<number, string> = {
  100: "Configuración",
  200: "Estructura",
  300: "Simulación",
  400: "Resultados",
  500: "Análisis",
};
const GROUP_ORDER = ["Configuración", "Estructura", "Simulación", "Resultados", "Análisis"];

function getGroupLabel(orden: number): string {
  const bucket = Math.floor(orden / 100) * 100;
  return ORDEN_GROUP_LABEL[bucket] ?? "Otros";
}

const PortalEstructuraComisionesLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { impersonatedUser, isImpersonating } = useProjectAdminImpersonation();
  const canImpersonate = profile?.puede_impersonar === true;
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { isPathAllowed, isSuperAdmin: hasAllMenus, isLoading: isLoadingPerms } = useAllowedMenus();
  const { data: ecSubmenus, isLoading: isLoadingSubmenus } = useEstructuraComisionesSubmenus();

  const visibleGroups = useMemo<NavGroup[]>(() => {
    if (isLoadingPerms || isLoadingSubmenus || !ecSubmenus) return [];

    const grouped = new Map<string, NavItem[]>();
    for (const s of ecSubmenus) {
      // `PATH_ICONS` hace de allowlist: un submenú dado de alta en BD cuya ruta
      // todavía no existe en `App.tsx` no debe pintarse, porque llevaría a una
      // pantalla en blanco. El precio es que olvidar el ícono de una vista que
      // SÍ existe la borra del menú sin ningún síntoma —así estuvo perdido
      // "Historial de Comisiones"—, de modo que en desarrollo se avisa.
      const icon = PATH_ICONS[s.vista_front_end];
      if (!icon) {
        if (import.meta.env.DEV) {
          console.warn(
            `[Estructura de comisiones] El submenú "${s.nombre}" (${s.vista_front_end}) ` +
            'está activo en BD pero no aparece en el menú: falta su ícono en PATH_ICONS.',
          );
        }
        continue;
      }
      if (!isPathAllowed(s.vista_front_end)) continue;
      const groupLabel = getGroupLabel(s.orden);
      if (!grouped.has(groupLabel)) grouped.set(groupLabel, []);
      grouped.get(groupLabel)!.push({ label: s.nombre, path: s.vista_front_end, icon });
    }

    return GROUP_ORDER
      .filter(g => grouped.has(g))
      .map(g => ({ label: g, items: grouped.get(g)! }));
  }, [ecSubmenus, isLoadingPerms, isLoadingSubmenus, isPathAllowed, hasAllMenus]);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");
  const currentSection = visibleGroups.flatMap(g => g.items).find(i => isActive(i.path))?.label || "Estructura de comisiones";

  const handleNavigate = (path: string) => { navigate(path); setMobileOpen(false); };

  const activeUserName = isImpersonating
    ? impersonatedUser?.nombre || impersonatedUser?.email || profile?.nombre || profile?.email || "Usuario"
    : profile?.nombre || profile?.email || "Usuario";

  const rawName = profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const userRole = profile?.rol_nombre ?? "Administrador";
  const initials = userName.split(" ").filter(Boolean).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join("") || "U";
  const fotoVista = useFotoPerfilVista();
  const activeInitials = activeUserName.split(" ").filter(Boolean).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join("") || "U";

  const sidebarContent = (
    <PortalSidebar
      portal="Portal Operación Comercial e Incentivos"
      cargando={isLoadingPerms || isLoadingSubmenus}
      groups={visibleGroups.map((group) => ({
        label: group.label,
        items: group.items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }))}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => handleNavigate("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <SimulatorProvider>
      <InventoryProvider>
        <CompetitorsProvider>
          <div className="min-h-screen flex antialiased">
            <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
              {sidebarContent}
            </aside>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
                {sidebarContent}
              </SheetContent>
            </Sheet>

            <div className="flex-1 lg:pl-64 min-w-0">
              <header className="hidden lg:flex items-center justify-between sticky top-0 z-20 bg-card border-b border-border-soft px-6 h-14">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <span className="font-medium">Estructura de comisiones</span>
                  <span className="text-muted-foreground">-</span>
                  <span className="text-muted-foreground">{currentSection}</span>
                </div>
                <div className="flex items-center gap-3">
                  {canImpersonate && <ProjectAdminImpersonationSelector />}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0 text-right">
                      <p className="text-sm font-medium text-foreground truncate">{activeUserName}</p>
                      <p className="text-xs text-muted-foreground truncate">Admin de Proyectos</p>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[12px] font-semibold">
                      {activeInitials}
                    </div>
                  </div>
                </div>
              </header>

              <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
                <div className="flex items-center justify-between px-3 h-14">
                  <div className="flex items-center gap-2 min-w-0">
                    <button onClick={() => setMobileOpen(true)} className="p-2 -ml-1 rounded-md text-foreground hover:bg-muted" aria-label="Abrir menú">
                      <Menu className="h-5 w-5" />
                    </button>
                    <p className="text-[15px] font-semibold text-foreground tracking-tight truncate">{currentSection}</p>
                  </div>
                  <AvatarPersona nombre={userName} fotoUrl={fotoVista} size="sm" />
                </div>
                {/* En móvil el "Ver como" va en su propia fila, a todo lo ancho (como Inmobiliaria). */}
                {canImpersonate && (
                  <div className="px-3 pb-3">
                    <ProjectAdminImpersonationSelector />
                  </div>
                )}
              </header>

              <main className="px-8 py-4 bg-background min-h-[calc(100vh-56px)]">
                <Outlet />
              </main>
            </div>
          </div>
        </CompetitorsProvider>
      </InventoryProvider>
    </SimulatorProvider>
  );
};

export const PortalEstructuraComisionesLayout = () => (
  <ProjectAdminImpersonationProvider>
    <PortalEstructuraComisionesLayoutInner />
  </ProjectAdminImpersonationProvider>
);

export default PortalEstructuraComisionesLayout;